import React, { useEffect, useRef } from 'react';
import ErrorBoundary from './ErrorBoundary';
import AppLayout from './layout/AppLayout';
import TabManager from './layout/TabManager';
import DialogManager from './layout/DialogManager';
import SearchPage from './search/SearchPage';
import ResultsPage from './search/ResultsPage';

// Custom hooks
import { useTabManager } from '../hooks/useTabManager';
import { useMonthData } from '../hooks/useMonthData';
import { useSearch } from '../hooks/useSearch';
import { useLocationData } from '../hooks/useLocationData';
import { useDisclaimer } from '../hooks/useDisclaimer';
import { useAuth } from '../hooks/useAuth';

// Contexts
import { useSearchContext } from '../contexts/SearchContext';
import { useDialogContext } from '../contexts/DialogContext';
import { useToast } from '../contexts/ToastContext';

function AppContent() {
  // Get today's date for reference
  const today = new Date();
  const formattedToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Use custom hooks for app functionality
  useAuth(); // Firebase auth state

  // Access enhanced search context with all the search management functionality
  const { 
    searchParams, 
    updateLocation,
    savedSearches,
    currentSavedSearch,
    saveCurrentSearch,
    loadSearch,
    updateLoadedSearch,
    deleteSearch,
    toggleAutoSave,
    checkAndAutoUpdateSearch
  } = useSearchContext();
  
  // Access dialog context
  const { dialogState, setSaveSearchDialogOpen, setDisclaimerDialogOpen } = useDialogContext();
  
  // Location data management with custom hook
  const {
    locationDetails,
    handleLocationSelect: originalHandleLocationSelect
  } = useLocationData();
  
  // Tab management with custom hook
  const { activeTab, handleTabChange, switchToTab } = useTabManager();
  
  // Disclaimer state with custom hook
  const {
    requireDisclaimerAcknowledgment,
    handleShowDisclaimer,
    handleCloseDisclaimerDialog,
  } = useDisclaimer();
  
  // Create a wrapper for handleLocationSelect that updates searchParams 
  const handleLocationSelect = (location: any | null) => {
    // Call the original handler from the hook
    originalHandleLocationSelect(location);
    
    // Update the search context
    updateLocation(location);
  };
  
  // Month data management
  const monthNavigation = useMonthData({
    locationId: searchParams?.location?.id ?? null,
    services: searchParams?.services ?? []
  });
  
  // Access toast context for notifications
  const { showToast } = useToast();

  // Search execution with custom hook
  const search = useSearch({
    searchParams: searchParams,
    monthData: monthNavigation.monthData,
    monthDataIsLoading: monthNavigation.isLoading,
    monthDataError: monthNavigation.error,
    currentSelectedDay: monthNavigation.selectedDay,
    setCurrentSelectedDay: monthNavigation.setSelectedDay,
    setViewDate: monthNavigation.setViewDate,
    refreshCurrentMonth: monthNavigation.refreshCurrentMonth,
    checkAndAutoUpdateSearch: checkAndAutoUpdateSearch, // Use the function from SearchContext
    setLocationDetails: () => {}, // This might need adjustment
    onSearchComplete: () => switchToTab(1) // Switch to results tab after search
  });
  
  // Handle load saved search - enhanced with additional UI logic
  const handleLoadSavedSearch = async (searchId: string) => {
    const success = await loadSearch(searchId);
    if (success) {
      // Clear previous results display state
      monthNavigation.setSelectedDay(null);
      // Close the dialog (assuming it was opened from SavedSearchesDialog)
      setDisclaimerDialogOpen(false);
      // Switch to Search tab (tab index 0) to see the loaded parameters
      switchToTab(0);
      // Show toast notification on successful load
      if (currentSavedSearch) {
        showToast(`Loaded search "${currentSavedSearch.name}"`, 'success');
      }
    }
  };
  
  // Prepare tab content
  const searchTabContent = (
    <ErrorBoundary>
      <SearchPage
        locationDetails={locationDetails}
        isLoading={search.isLoading}
        error={search.error}
        handleLocationSelect={handleLocationSelect}
        handleSearch={search.handleSearch}
      />
    </ErrorBoundary>
  );

  const resultsTabContent = (
    <ErrorBoundary>
      <ResultsPage
        isLoading={search.isLoading}
        schedules={search.schedules}
        activeSchedules={search.activeSchedules}
        currentSchedule={search.currentSchedule}
        currentOpportunities={search.currentOpportunities}
        showOptimized={search.showOptimized}
        monthDataIsLoading={monthNavigation.isLoading}
        currentSelectedDay={monthNavigation.selectedDay}
        selectedOpportunities={search.selectedOpportunities}
        selectedOpportunityIds={search.selectedOpportunityIds}
        formattedToday={formattedToday}
        handleSelectDate={search.handleSelectDate}
        handleMonthChange={(direction) => {
          direction === 'prev'
            ? monthNavigation.goToPreviousMonth()
            : monthNavigation.goToNextMonth();
        }}
        toggleOpportunitySelection={search.toggleOpportunitySelection}
        tideHeightStatistics={search.tideHeightStatistics}
      />
    </ErrorBoundary>
  );

  // Store a reference to the clearSearchResults function
  const searchContextRef = useRef(useSearchContext());
  
  // Update the context's clearSearchResults function when search.clearSearchResults changes
  useEffect(() => {
    // This is a workaround to avoid having nested SearchProviders
    // We're directly updating the existing SearchContext's clearSearchResults property
    if (searchContextRef.current && search.clearSearchResults) {
      searchContextRef.current.clearSearchResults = search.clearSearchResults;
    }
  }, [search.clearSearchResults]);

  return (
    <AppLayout
      title="Tide Analyzer"
      onShowDisclaimer={handleShowDisclaimer}
    >
      <TabManager
        tabs={[
          { label: "Search", content: searchTabContent },
          { 
            label: "Results", 
            disabled: !search.hasSearchBeenInitiated || 
                     Object.keys(search.schedules).length === 0 || 
                     !searchParams?.services || 
                     searchParams.services.length === 0, 
            content: resultsTabContent 
          }
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        showOptimizedToggle={search.hasSearchBeenInitiated && Object.keys(search.schedules).length > 0}
        showOptimized={search.showOptimized}
        onToggleOptimized={search.setShowOptimized}
        optimizing={search.optimizing}
      />

      {/* Dialog Manager - Centralizes all dialogs */}
      <DialogManager
        saveSearchDialogOpen={dialogState.saveSearchDialogOpen}
        showSavedSearches={dialogState.showSavedSearches}
        updateSearchDialogOpen={dialogState.updateSearchDialogOpen}
        disclaimerDialogOpen={dialogState.disclaimerDialogOpen}
        savedSearches={savedSearches} // Use savedSearches from SearchContext
        currentLoadedSearchName={currentSavedSearch?.name || ''} // Use currentSavedSearch from SearchContext
        onSaveSearchWithName={saveCurrentSearch} // Use saveCurrentSearch from SearchContext
        onLoadSavedSearch={handleLoadSavedSearch} // Use our enhanced handler
        onUpdateSearch={updateLoadedSearch} // Use updateLoadedSearch from SearchContext
        onDeleteSavedSearch={deleteSearch} // Use deleteSearch from SearchContext
        onCloseSaveSearchDialog={() => setSaveSearchDialogOpen(false)}
        onCloseSavedSearchesDialog={() => setDisclaimerDialogOpen(false)}
        onCloseUpdateSearchDialog={() => setSaveSearchDialogOpen(false)}
        onCloseDisclaimerDialog={handleCloseDisclaimerDialog}
        requireDisclaimerAcknowledgment={requireDisclaimerAcknowledgment}
      />
    </AppLayout>
  );
}

export default AppContent;
